import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';
import { queryClient } from './lib/queryClient';

// Layouts
import AuthLayout from './components/layout/AuthLayout';
import DashboardLayout from './components/layout/DashboardLayout';
import PublicLayout from './components/layout/PublicLayout';
import CompliantLayout from './components/layout/CompliantLayout';
import { AdminRoute } from './components/layout/AdminRoute';
import { PublicRoute } from './components/layout/PublicRoute';
import { LanguageWrapper } from './components/LanguageWrapper';

// Analytics
import Analytics from './components/analytics/Analytics';
import ScrollToTop from './components/ScrollToTop';
import PWAInstallPrompt from './components/PWAInstallPrompt';

// Public Pages
import HomePage from './pages/public/HomePage';
import PricingPage from './pages/public/PricingPage';
import PublicFaqPage from './pages/public/PublicFaqPage';
import AboutPage from './pages/public/AboutPage';
import ContactPage from './pages/public/ContactPage';
import NotFoundPage from './pages/public/NotFoundPage';
import PrivacyPolicyPage from './pages/public/PrivacyPolicyPage';
import TermsOfServicePage from './pages/public/TermsOfServicePage';
import CookiePolicyPage from './pages/public/CookiePolicyPage';
import AdspectComparisonPage from './pages/public/AdspectComparisonPage';
import ClickFraudProtectionPage from './pages/public/ClickFraudProtectionPage';
import TrafficQualityPage from './pages/public/TrafficQualityPage';
import BlogTrafficQualityScorePage from './pages/public/BlogTrafficQualityScorePage';
import BlogTrafficQualityScorePageRU from './pages/public/BlogTrafficQualityScorePageRU';
import BlogListPage from './pages/public/BlogListPage';
import BlogPostPage from './pages/public/BlogPostPage';
import BlogFacebookAdBansPage from './pages/public/BlogFacebookAdBansPage';
import BlogMLBotDetectionPage from './pages/public/BlogMLBotDetectionPage';
import BlogMLBotDetectionPageRU from './pages/public/BlogMLBotDetectionPageRU';
import DemoPage from './pages/public/DemoPage';
import UnsubscribePage from './pages/public/UnsubscribePage';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Dashboard Pages
import DashboardPage from './pages/dashboard/DashboardPage';

// Campaign Pages
import CampaignListPage from './pages/campaigns/CampaignListPage';
import CampaignCreatePage from './pages/campaigns/CampaignCreatePage';
import CampaignDetailPage from './pages/campaigns/CampaignDetailPage';
import CampaignEditPage from './pages/campaigns/CampaignEditPage';
import IntegrationDownloadPage from './pages/campaigns/IntegrationDownloadPage';
import CampaignTrafficQualityPage from './pages/campaigns/TrafficQualityPage';
import AccountTrafficQualityPage from './pages/statistics/AccountTrafficQualityPage';
import { TagManagerPage } from './pages/campaigns/TagManagerPage';

// Statistics Pages
import StatisticsPage from './pages/statistics/StatisticsPage';

// Team Pages
import TeamPage from './pages/team/TeamPage';
import AcceptInvitationPage from './pages/team/AcceptInvitationPage';

// Settings Pages
import AccountSettingsPage from './pages/settings/AccountSettingsPage';
import ApiKeysPage from './pages/settings/ApiKeysPage';
import ApiDocsPage from './pages/settings/ApiDocsPage';

// Subscription Pages
import SubscriptionManagementPage from './pages/subscription/SubscriptionManagementPage';

// Profile Pages
import UserProfilePage from './pages/profile/UserProfilePage';
import ReferralPage from './pages/referrals/ReferralPage';

// Ban Pages
import BanListPage from './pages/bans/BanListPage';

// FAQ Pages
import FaqPage from './pages/faq/FaqPage';

// Ticket Pages
import { TicketListPage } from './pages/tickets/TicketListPage';
import { TicketDetailPage } from './pages/tickets/TicketDetailPage';
import { CreateTicketPage } from './pages/tickets/CreateTicketPage';

// Admin Pages
import {
  UserManagementPage,
  AccountManagementPage,
  SystemStatsPage,
  PaymentManagementPage,
  SubscriptionPlanManagementPage,
  QueueManagementPage,
  FaqManagementPage,
  BlogManagementPage,
  NewsletterManagementPage,
  ReferralAdminDashboardPage,
  RateLimitSettings,
  AdminTicketListPage,
  AdminTicketDetailPage,
  TicketTemplateManagementPage,
  PromoCodeManagementPage,
} from './pages/admin';
import { AdminNotificationsPage } from './pages/admin/AdminNotificationsPage';
import BanManagementPage from './pages/admin/BanManagementPage';
import AdminCampaignStatisticsPage from './pages/admin/AdminCampaignStatisticsPage';
import AdminTrafficQualityPage from './pages/admin/AdminTrafficQualityPage';
import { TgChannelManagementPage } from './pages/admin/TgChannelManagementPage';
import AdminRequestStatsPage from './pages/admin/AdminRequestStatsPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Get saved language preference
    const savedLang = localStorage.getItem('axetra_preferred_language') || 'en';
    return <Navigate to={`/${savedLang}/login`} replace />;
  }

  return <>{children}</>;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Initialize auth check once when module loads
let authCheckInitialized = false;
const initializeAuth = () => {
  if (!authCheckInitialized) {
    authCheckInitialized = true;
    useAuthStore.getState().fetchUser();
  }
};

function BlogMLBotDetectionRouter() {
  const { lang } = useParams<{ lang: string }>();
  return lang === 'ru' ? <BlogMLBotDetectionPageRU /> : <BlogMLBotDetectionPage />;
}

function BlogTrafficQualityScoreRouter() {
  const { lang } = useParams<{ lang: string }>();
  return lang === 'ru' ? <BlogTrafficQualityScorePageRU /> : <BlogTrafficQualityScorePage />;
}

function AppContent() {
  useEffect(() => {
    initializeAuth();
  }, []);

  return (
    <Routes>
      {/* Language-specific Auth Routes */}
      <Route
        path="/:lang/login"
        element={
          <LanguageWrapper>
            <GuestRoute>
              <AuthLayout>
                <LoginPage />
              </AuthLayout>
            </GuestRoute>
          </LanguageWrapper>
        }
      />
      <Route
        path="/:lang/register"
        element={
          <LanguageWrapper>
            <GuestRoute>
              <AuthLayout>
                <RegisterPage />
              </AuthLayout>
            </GuestRoute>
          </LanguageWrapper>
        }
      />
      <Route
        path="/:lang/forgot-password"
        element={
          <LanguageWrapper>
            <GuestRoute>
              <AuthLayout>
                <ForgotPasswordPage />
              </AuthLayout>
            </GuestRoute>
          </LanguageWrapper>
        }
      />
      <Route
        path="/:lang/reset-password"
        element={
          <LanguageWrapper>
            <ResetPasswordPage />
          </LanguageWrapper>
        }
      />

      {/* Legacy Auth Routes (redirect to language-specific) */}
      <Route path="/login" element={<Navigate to="/en/login" replace />} />
      <Route path="/register" element={<Navigate to="/en/register" replace />} />
      <Route path="/forgot-password" element={<Navigate to="/en/forgot-password" replace />} />
      <Route path="/reset-password" element={<Navigate to="/en/reset-password" replace />} />

      {/* Email Verification */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <DashboardPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Language-specific FAQ */}
      <Route
        path="/:lang/faq"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <PublicLayout>
                <PublicFaqPage />
              </PublicLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />

      {/* Legacy FAQ route - redirect to /en */}
      <Route path="/faq" element={<Navigate to="/en/faq" replace />} />
      <Route
        path="/support/faq"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <FaqPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CampaignListPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/new"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CampaignCreatePage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/tags"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <TagManagerPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CampaignDetailPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/:id/edit"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CampaignEditPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/:id/integration"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <IntegrationDownloadPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/:id/traffic-quality"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CampaignTrafficQualityPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/traffic-quality"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AccountTrafficQualityPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/statistics"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <StatisticsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <TeamPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AccountSettingsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/api-keys"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ApiKeysPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/api-docs"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ApiDocsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <SubscriptionManagementPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <UserProfilePage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ReferralPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Ticket Routes */}
      <Route
        path="/tickets"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <TicketListPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tickets/new"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateTicketPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tickets/:ticketId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <TicketDetailPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Ban Routes */}
      <Route
        path="/bans"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <BanListPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <DashboardLayout>
              <UserManagementPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/accounts"
        element={
          <AdminRoute>
            <DashboardLayout>
              <AccountManagementPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/stats"
        element={
          <AdminRoute>
            <DashboardLayout>
              <SystemStatsPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/campaign-statistics"
        element={
          <AdminRoute>
            <DashboardLayout>
              <AdminCampaignStatisticsPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/traffic-quality"
        element={
          <AdminRoute>
            <DashboardLayout>
              <AdminTrafficQualityPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/request-stats"
        element={
          <AdminRoute>
            <DashboardLayout>
              <AdminRequestStatsPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/payments"
        element={
          <AdminRoute>
            <DashboardLayout>
              <PaymentManagementPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/subscription-plans"
        element={
          <AdminRoute>
            <DashboardLayout>
              <SubscriptionPlanManagementPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/queues"
        element={
          <AdminRoute>
            <DashboardLayout>
              <QueueManagementPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/faq"
        element={
          <AdminRoute>
            <DashboardLayout>
              <FaqManagementPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/blog"
        element={
          <AdminRoute>
            <DashboardLayout>
              <BlogManagementPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/newsletter"
        element={
          <AdminRoute>
            <DashboardLayout>
              <NewsletterManagementPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/referrals"
        element={
          <AdminRoute>
            <DashboardLayout>
              <ReferralAdminDashboardPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/rate-limits"
        element={
          <AdminRoute>
            <DashboardLayout>
              <RateLimitSettings />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/bans"
        element={
          <AdminRoute>
            <DashboardLayout>
              <BanManagementPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/notifications"
        element={
          <AdminRoute>
            <DashboardLayout>
              <AdminNotificationsPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/tickets"
        element={
          <AdminRoute>
            <DashboardLayout>
              <AdminTicketListPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/tickets/:ticketId"
        element={
          <AdminRoute>
            <DashboardLayout>
              <AdminTicketDetailPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/ticket-templates"
        element={
          <AdminRoute>
            <DashboardLayout>
              <TicketTemplateManagementPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/promo-codes"
        element={
          <AdminRoute>
            <DashboardLayout>
              <PromoCodeManagementPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/tg-channels"
        element={
          <AdminRoute>
            <DashboardLayout>
              <TgChannelManagementPage />
            </DashboardLayout>
          </AdminRoute>
        }
      />

      {/* Root redirect to language-specific homepage */}
      <Route path="/" element={<Navigate to="/en" replace />} />

      {/* Language-specific Public Homepage */}
      <Route
        path="/:lang"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <PublicLayout>
                <HomePage />
              </PublicLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />

      {/* Language-specific Public Pages */}
      <Route
        path="/:lang/pricing"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <PublicLayout>
                <PricingPage />
              </PublicLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />

      <Route
        path="/:lang/about"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <PublicLayout>
                <AboutPage />
              </PublicLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />

      <Route
        path="/:lang/contact"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <PublicLayout>
                <ContactPage />
              </PublicLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />

      {/* Legacy routes without language prefix - redirect to /en */}
      <Route path="/pricing" element={<Navigate to="/en/pricing" replace />} />
      <Route path="/about" element={<Navigate to="/en/about" replace />} />
      <Route path="/contact" element={<Navigate to="/en/contact" replace />} />

      {/* Language-specific Legal Pages */}
      {/* Legal pages use CompliantLayout: they are policy-clean and are linked
          from paid-ad landing pages, so their nav must not reach cloaking content */}
      <Route
        path="/:lang/privacy"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <CompliantLayout>
                <PrivacyPolicyPage />
              </CompliantLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />
      <Route
        path="/:lang/terms"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <CompliantLayout>
                <TermsOfServicePage />
              </CompliantLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />
      <Route
        path="/:lang/cookies"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <CompliantLayout>
                <CookiePolicyPage />
              </CompliantLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />

      {/* Legacy legal routes - redirect to /en */}
      <Route path="/privacy" element={<Navigate to="/en/privacy" replace />} />
      <Route path="/terms" element={<Navigate to="/en/terms" replace />} />
      <Route path="/cookies" element={<Navigate to="/en/cookies" replace />} />

      {/* Language-specific Comparison Pages */}
      <Route
        path="/:lang/vs/adspect"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <PublicLayout>
                <AdspectComparisonPage />
              </PublicLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />

      {/* Language-specific Landing Pages */}
      <Route
        path="/:lang/zashita-ot-sklikivaniya"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <PublicLayout>
                <ClickFraudProtectionPage />
              </PublicLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />
      <Route
        path="/:lang/click-fraud-protection"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <PublicLayout>
                <ClickFraudProtectionPage />
              </PublicLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />
      {/* Paid-ad landing page (Campaign A) — uses CompliantLayout so no nav
          path reaches cloaking content and the page passes Google Ads review */}
      <Route
        path="/:lang/traffic-quality"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <CompliantLayout>
                <TrafficQualityPage />
              </CompliantLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />
      <Route path="/traffic-quality" element={<Navigate to="/en/traffic-quality" replace />} />

      {/* Language-specific Blog Pages */}
      <Route
        path="/:lang/blog"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <PublicLayout>
                <BlogListPage />
              </PublicLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />
      <Route
        path="/:lang/blog/how-to-prevent-facebook-ad-bans"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <PublicLayout>
                <BlogFacebookAdBansPage />
              </PublicLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />

      {/* Language-specific Demo Page */}
      <Route
        path="/:lang/demo"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <PublicLayout>
                <DemoPage />
              </PublicLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />

      {/* Legacy demo route - redirect to /en */}
      <Route path="/demo" element={<Navigate to="/en/demo" replace />} />

      <Route
        path="/:lang/blog/machine-learning-bot-detection"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <PublicLayout>
                <BlogMLBotDetectionRouter />
              </PublicLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />

      <Route
        path="/:lang/blog/axetra-vs-adspect"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <PublicLayout>
                <AdspectComparisonPage />
              </PublicLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />
      <Route
        path="/:lang/blog/click-fraud-protection"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <PublicLayout>
                <ClickFraudProtectionPage />
              </PublicLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />

      {/* Paid-ad blog article (Campaign A destination) — CompliantLayout so no
          nav path reaches cloaking content; must precede the generic :slug route */}
      <Route
        path="/:lang/blog/traffic-quality-score"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <CompliantLayout>
                <BlogTrafficQualityScoreRouter />
              </CompliantLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />
      <Route path="/blog/traffic-quality-score" element={<Navigate to="/en/blog/traffic-quality-score" replace />} />

      {/* Generic DB-driven blog post renderer - must come after all specific blog routes */}
      <Route
        path="/:lang/blog/:slug"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <PublicLayout>
                <BlogPostPage />
              </PublicLayout>
            </PublicRoute>
          </LanguageWrapper>
        }
      />

      {/* Legacy blog/comparison routes - redirect to /en */}
      <Route path="/vs/adspect" element={<Navigate to="/en/vs/adspect" replace />} />
      <Route path="/blog" element={<Navigate to="/en/blog" replace />} />
      <Route path="/blog/how-to-prevent-facebook-ad-bans" element={<Navigate to="/en/blog/how-to-prevent-facebook-ad-bans" replace />} />
      <Route path="/blog/machine-learning-bot-detection" element={<Navigate to="/en/blog/machine-learning-bot-detection" replace />} />
      <Route path="/blog/axetra-vs-adspect" element={<Navigate to="/en/blog/axetra-vs-adspect" replace />} />
      <Route path="/blog/click-fraud-protection" element={<Navigate to="/en/blog/click-fraud-protection" replace />} />

      {/* Newsletter Unsubscribe */}
      <Route
        path="/unsubscribe"
        element={
          <PublicRoute>
            <UnsubscribePage />
          </PublicRoute>
        }
      />

      {/* Fallback for unknown routes */}
      <Route
        path="*"
        element={
          <PublicLayout>
            <NotFoundPage />
          </PublicLayout>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ScrollToTop />
        <Analytics />
        <AppContent />
        <PWAInstallPrompt />
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </QueryClientProvider>
  );
}

export default App;
